package com.arenaproplay.admin.ui.components.charts.svg

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/**
 * Night Arena funnel bar chart. Ported from kontaq QpFunnel (PlatformCharts.kt)
 * and styled per the mockup night-arena-blue.html.
 *
 * - Step 0: full-width Primary gradient (#0061FF → #4D8CFF)
 * - Middle steps: proportional width, dimmed blue fill
 * - Last step: Yellow gradient (#E4CD40 → #F0DE6B) with dark text
 * - Width transition .5s ease for polish
 * - min-width 6% clamp (kontaq convention); min-width 150px on last step only
 * - Narrow bars (< 25%): label hidden inside bar, value shown right-aligned outside
 *
 * @param steps    ordered funnel steps from widest to narrowest
 * @param modifier standard Kobweb modifier
 */
@Composable
fun FunnelBars(
    steps: List<FunnelStep>,
    modifier: Modifier = Modifier,
) {
    if (steps.isEmpty()) {
        Div(attrs = {
            style {
                property("height", "60px")
                property("background", "rgba(255,255,255,.02)")
                property("border-radius", "8px")
            }
        })
        return
    }

    val topVal = steps.first().value.coerceAtLeast(1L)
    val lastIdx = steps.size - 1

    Column(modifier.fillMaxWidth().gap(6.px)) {
        steps.forEachIndexed { i, step ->
            // M2: min 6% (kontaq convention); 16% was too wide for small steps
            val pct = (step.value * 100L / topVal).coerceIn(6L, 100L)
            val isFirst = i == 0
            val isLast = i == lastIdx

            val background = when {
                step.colorHex != null -> "linear-gradient(90deg,${step.colorHex},${step.colorHex}BB)"
                isLast -> "linear-gradient(90deg,#E4CD40,#F0DE6B)"
                isFirst -> "linear-gradient(90deg,rgba(0,97,255,.85),rgba(77,140,255,.55))"
                else -> {
                    // Proportionally fade opacity so mid-funnel bars feel lighter
                    val opacity = 0.55 - 0.15 * (i.toDouble() / lastIdx.coerceAtLeast(1))
                    "linear-gradient(90deg,rgba(0,97,255,${fmt(opacity)}),rgba(77,140,255,${fmt(opacity * 0.65)}))"
                }
            }

            val textColor = when {
                step.textColorHex != null -> step.textColorHex
                isLast -> "#1A1608"
                isFirst -> "#FFFFFF"
                else -> "#F0F4FC"
            }

            // M2: bars < 25% wide are "narrow" — move value outside for readability
            val isNarrow = pct < 25L && !isLast

            // Each step row: bar pill + (optional) outside value label
            // Bug-class 6 fix: overflow:hidden on the row so the bar pill
            // (which uses flex-shrink:0) can't visually overflow into adjacent sections
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "8px")
                    property("width", "100%")
                    property("overflow", "hidden")
                    property("min-width", "0")
                }
            }) {
                // Bar pill
                Div(attrs = {
                    style {
                        property("width", "${pct}%")
                        // M2: min-width capped at 40% to prevent the pill escaping
                        // a narrow two-column grid cell
                        if (isLast) property("min-width", "min(150px, 40%)")
                        property("height", "34px")
                        property("border-radius", "10px")
                        property("background", background)
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "space-between")
                        property("padding", "0 14px")
                        property("transition", "width .5s ease")
                        property("box-sizing", "border-box")
                        property("overflow", "hidden")
                        // Bug-class 6 fix: allow shrinking so bars in narrow grid
                        // cells don't overflow; visual size is already set by width%
                        property("flex-shrink", "1")
                    }
                }) {
                    // Label: hide inside narrow bars to avoid crowding
                    if (!isNarrow) {
                        Div(attrs = {
                            style {
                                property("font-size", "11.5px")
                                property("font-weight", "600")
                                property("color", textColor)
                                property("font-family", "'Inter',sans-serif")
                                property("white-space", "nowrap")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("max-width", "70%")
                            }
                        }) {
                            Text(step.label)
                        }
                        Div(attrs = {
                            style {
                                property("font-size", "12px")
                                property("font-weight", "700")
                                property("color", textColor)
                                property("font-family", "'Unbounded',sans-serif")
                                property("white-space", "nowrap")
                                property("flex-shrink", "0")
                            }
                        }) {
                            Text(step.value.toString())
                        }
                    }
                }

                // Outside value label for narrow bars — right-aligned after the pill
                if (isNarrow) {
                    Div(attrs = {
                        style {
                            property("font-size", "11.5px")
                            property("font-weight", "700")
                            property("color", "rgba(240,244,252,.65)")
                            property("font-family", "'Unbounded',sans-serif")
                            property("white-space", "nowrap")
                        }
                    }) {
                        Text(step.value.toString())
                    }
                }
            }
        }
    }
}
